import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { fetchDictBatch } from '@/api/dict';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/hooks/useAuth';
import { useCollect } from '@/hooks/useCollect';
import { useDownload } from '@/hooks/useDownload';
import { useHistory } from '@/hooks/useHistory';
import { usePoints } from '@/hooks/usePoints';
import LevelCard from '@/pages/member/components/LevelCard';
import DailyCheckIn from '@/pages/member/components/DailyCheckIn';
import InviteFriend from '@/pages/member/components/InviteFriend';
import AnnouncementCenter from '@/pages/member/components/AnnouncementCenter';
import { getPublicSolutions } from '@/api/solution';
import { getPublicTools, presignUploadFile, completeUploadFile } from '@/api/tool';
import { changeMyPassword, updateMyProfile, deactivateMyAccount } from '@/api/auth';
import { getPublicPointsOverview, type PublicPointsRule } from '@/api/points';
import { getMemberStats } from '@/api/interaction';
import { dispatchToast } from '@/components/ui/GlobalToast';
import { MAX_COVER_IMAGE_SIZE_BYTES } from '@/utils/uploadLimits';


type Tab = 'profile' | 'favorites' | 'downloads' | 'history' | 'points' | 'announcements' | 'settings';

// ── Sensitive word check ──────────────────────────────────────────
const FORBIDDEN_WORDS = ['管理员', '超级管理员', 'admin', 'administrator', 'superadmin', '管理', '系统', 'system', 'root'];
const CN_MOBILE_PHONE_REGEX = /^(?:\+?86)?1[3-9]\d{9}$/;
function containsForbiddenWord(str: string): boolean {
  const lower = str.toLowerCase().replace(/\s/g, '');
  return FORBIDDEN_WORDS.some((w) => lower.includes(w.toLowerCase()));
}

export default function MemberPage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [dictTiers, setDictTiers] = useState<{ label: string; value: string }[]>([]);
  const [dictSolutionCategories, setDictSolutionCategories] = useState<{ label: string; value: string }[]>([]);
  const [dictSolutionDifficulties, setDictSolutionDifficulties] = useState<{ label: string; value: string; color?: string | null }[]>([]);
  const [dictToolCategories, setDictToolCategories] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    fetchDictBatch(['member_tier', 'solution_category', 'solution_difficulty', 'tool_category'])
      .then((dicts) => {
        if (dicts['member_tier']) {
          setDictTiers(dicts['member_tier'].map((x) => ({ label: x.label, value: x.value })));
        }
        if (dicts['solution_category']) {
          setDictSolutionCategories(dicts['solution_category'].map((x) => ({ label: x.label, value: x.value })));
        }
        if (dicts['solution_difficulty']) {
          setDictSolutionDifficulties(dicts['solution_difficulty'].map((x) => ({ label: x.label, value: x.value, color: x.color })));
        }
        if (dicts['tool_category']) {
          setDictToolCategories(dicts['tool_category'].map((x) => ({ label: x.label, value: x.value })));
        }
      })
      .catch((err) => console.error('Failed to load member center dictionaries:', err));
  }, []);

  const getTierLabel = useCallback((tier: string) => {
    if (!tier) return '无';
    const found = dictTiers.find((t) => t.value === tier);
    return found ? found.label : tier;
  }, [dictTiers]);
  const getSolutionCategoryLabel = useCallback((category: string) => {
    if (!category) return '';
    const found = dictSolutionCategories.find((c) => c.value === category);
    return found ? found.label : category;
  }, [dictSolutionCategories]);
  const getSolutionDifficultyItem = useCallback((difficulty: string) => {
    if (!difficulty) return null;
    return dictSolutionDifficulties.find((d) => d.value === difficulty || d.label === difficulty) || null;
  }, [dictSolutionDifficulties]);
  const getSolutionDifficultyLabel = useCallback((difficulty: string) => {
    const found = getSolutionDifficultyItem(difficulty);
    return found ? found.label : difficulty;
  }, [getSolutionDifficultyItem]);
  const getToolCategoryLabel = useCallback((category: string) => {
    if (!category) return '';
    const found = dictToolCategories.find((c) => c.value === category);
    return found ? found.label : category;
  }, [dictToolCategories]);
  const { solutionIds, toolIds, remove, refresh: refreshCollect } = useCollect({ lazy: true });
  const { records: downloadRecords, removeRecord: removeDownload, refresh: refreshDownloads } = useDownload({ lazy: true });
  const { items: historyItems, remove: removeHistory, clearAll: clearHistory, refresh: refreshHistory } = useHistory({ lazy: true });
  const {
    points,
    history: pointsHistory,
    levelInfo,
    levels,
    progressToNext,
    nextLevel,
    loading: pointsLoading,
    error: pointsError,
    refresh: refreshPoints,
  } = usePoints({ lazy: true });

  const [stats, setStats] = useState<{
    totalFavoritesCount: number;
    totalDownloadsCount: number;
    currentPoints: number;
  } | null>(null);

  const loadStats = useCallback(async () => {
    if (!user) return;
    try {
      const res = await getMemberStats();
      setStats(res);
    } catch (err) {
      console.error('Failed to load member stats:', err);
    }
  }, [user]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    window.addEventListener('wanxiang_favorites_updated', loadStats);
    window.addEventListener('wanxiang_downloads_updated', loadStats);
    window.addEventListener('wanxiang_points_updated', loadStats);
    window.addEventListener('wanxiang_profile_updated', loadStats);
    return () => {
      window.removeEventListener('wanxiang_favorites_updated', loadStats);
      window.removeEventListener('wanxiang_downloads_updated', loadStats);
      window.removeEventListener('wanxiang_points_updated', loadStats);
      window.removeEventListener('wanxiang_profile_updated', loadStats);
    };
  }, [loadStats]);


  const [solutionPool, setSolutionPool] = useState<any[]>([]);
  const [toolPool, setToolPool] = useState<any[]>([]);
  const [pointsRules, setPointsRules] = useState<PublicPointsRule[]>([]);

  useEffect(() => {
    let active = true;
    const loadPools = async () => {
      try {
        const [solutionsRes, toolsRes] = await Promise.all([
          getPublicSolutions(),
          getPublicTools(),
        ]);
        if (!active) return;
        if (!Array.isArray(solutionsRes.list) || !Array.isArray(toolsRes.list)) {
          throw new Error('收藏资源池响应格式错误');
        }
        setSolutionPool(solutionsRes.list);
        setToolPool(toolsRes.list);
      } catch (err) {
        console.error('Failed to load member pools:', err);
        if (!active) return;
        setSolutionPool([]);
        setToolPool([]);
      }
    };
    void loadPools();
    const refreshByEvent = () => { void loadPools(); };
    window.addEventListener('wanxiang_solutions_updated', refreshByEvent);
    window.addEventListener('wanxiang_tools_updated', refreshByEvent);
    return () => {
      active = false;
      window.removeEventListener('wanxiang_solutions_updated', refreshByEvent);
      window.removeEventListener('wanxiang_tools_updated', refreshByEvent);
    };
  }, []);

  const [switches, setSwitches] = useState<{
    invite: boolean;
    like: boolean;
    share: boolean;
    checkin: boolean;
  } | null>(null);
  const [switchesLoading, setSwitchesLoading] = useState(true);
  const [switchesError, setSwitchesError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const syncSwitches = async () => {
      setSwitchesLoading(true);
      setSwitchesError(null);
      try {
        const overview = await getPublicPointsOverview(1);
        if (!active) return;
        const features = overview.features;
        if (
          typeof features?.invite !== 'boolean'
          || typeof features?.like !== 'boolean'
          || typeof features?.share !== 'boolean'
          || typeof features?.checkin !== 'boolean'
        ) {
          throw new Error('功能开关响应格式错误');
        }
        setSwitches({
          invite: features.invite,
          like: features.like,
          share: features.share,
          checkin: features.checkin,
        });
        const rules = Array.isArray(overview.pointsRules)
          ? overview.pointsRules.filter((item): item is PublicPointsRule => Boolean(item && item.enabled))
          : [];
        setPointsRules(rules);
      } catch (err) {
        if (!active) return;
        setSwitches(null);
        setPointsRules([]);
        setSwitchesError(err instanceof Error ? err.message : '功能开关加载失败');
      } finally {
        if (active) setSwitchesLoading(false);
      }
    };

    void syncSwitches();
    const refreshByEvent = () => { void syncSwitches(); };
    window.addEventListener('wanxiang_feature_updated', refreshByEvent);
    window.addEventListener('wanxiang_settings_updated', refreshByEvent);
    return () => {
      active = false;
      window.removeEventListener('wanxiang_feature_updated', refreshByEvent);
      window.removeEventListener('wanxiang_settings_updated', refreshByEvent);
    };
  }, []);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = searchParams.get('tab') as Tab | null;
    const valid: Tab[] = ['profile', 'favorites', 'downloads', 'history', 'points', 'announcements', 'settings'];
    return t && valid.includes(t) ? t : 'profile';
  });

  useEffect(() => {
    const t = searchParams.get('tab') as Tab | null;
    const valid: Tab[] = ['profile', 'favorites', 'downloads', 'history', 'points', 'announcements', 'settings'];
    if (t && valid.includes(t)) {
      setActiveTab(t);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    if (activeTab === 'favorites') {
      void refreshCollect();
    } else if (activeTab === 'downloads') {
      void refreshDownloads();
    } else if (activeTab === 'history') {
      void refreshHistory();
    } else if (activeTab === 'points') {
      void refreshPoints();
    }
  }, [activeTab, refreshCollect, refreshDownloads, refreshHistory, refreshPoints]);

  // 个人信息编辑表单状态
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: (user as any)?.company || '',
    position: (user as any)?.position || '',
    phone: (user as any)?.phone || '',
    daohao: (user as any)?.daohao || '',
  });
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    setProfileForm({
      name: user?.name || '',
      email: user?.email || '',
      company: (user as any)?.company || '',
      position: (user as any)?.position || '',
      phone: (user as any)?.phone || '',
      daohao: (user as any)?.daohao || '',
    });
  }, [user]);

  const applyUserSnapshot = (nextUser: any) => {
    login(nextUser);
    setProfileForm({
      name: nextUser.name || '',
      email: nextUser.email || '',
      company: nextUser.company || '',
      position: nextUser.position || '',
      phone: nextUser.phone || '',
      daohao: nextUser.daohao || '',
    });
    setNamePublic(Boolean(nextUser.namePublic));
    setAvatarUrl(nextUser.avatarUrl || '');
    window.dispatchEvent(new CustomEvent('wanxiang_profile_updated'));
  };

  const handleProfileSave = async () => {
    if (!user) {
      setProfileError('请先登录');
      return;
    }
    if (!profileForm.name.trim()) {
      setProfileError('姓名不能为空');
      return;
    }
    if (profileForm.name.trim().length < 2) {
      setProfileError('姓名至少 2 个字符');
      return;
    }
    if (containsForbiddenWord(profileForm.name)) {
      setProfileError('姓名不能包含「管理员」等敏感词汇');
      return;
    }
    if (profileForm.daohao.trim()) {
      if (profileForm.daohao.trim().length < 2) {
        setProfileError('道号至少 2 个字符');
        return;
      }
      if (containsForbiddenWord(profileForm.daohao)) {
        setProfileError('道号不能包含「管理员」等敏感词汇');
        return;
      }
    }

    const trimmedName = profileForm.name.trim();
    const trimmedDaohao = profileForm.daohao.trim();
    const trimmedPhone = profileForm.phone.trim();
    if (namePublic && !trimmedDaohao) {
      setProfileError('请先填写对外道号，再开启公开信息');
      dispatchToast('error', '请先填写对外道号，再开启公开信息');
      return;
    }
    if (trimmedPhone && !CN_MOBILE_PHONE_REGEX.test(trimmedPhone)) {
      setProfileError('手机号格式不正确');
      return;
    }
    setProfileError('');
    setProfileSaving(true);
    try {
      const updatedUser = await updateMyProfile({
        name: trimmedName,
        daohao: trimmedDaohao || null,
        company: profileForm.company.trim() || null,
        position: profileForm.position.trim() || null,
        phone: trimmedPhone || null,
      });
      applyUserSnapshot(updatedUser);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : '保存失败，请稍后重试');
    } finally {
      setProfileSaving(false);
    }
  };

  const favoriteSolutions = solutionPool.filter((s) => solutionIds.includes(String(s.id)));
  const favoriteTools = toolPool.filter((t) => toolIds.includes(String(t.id)));
  const totalFavs = favoriteSolutions.length + favoriteTools.length;

  const difficultyOptions = ['全部', ...[...new Set(favoriteSolutions.map((s) => s.difficulty))].filter(Boolean)];
  const toolCategoryOptions = ['全部', ...[...new Set(favoriteTools.map((t) => t.category))].filter(Boolean)];

  const downloadedTools = downloadRecords.filter((record) => record.targetType === 'tool' || record.targetType === 'solution');

  const [namePublic, setNamePublic] = useState(Boolean((user as any)?.namePublic));

  useEffect(() => {
    setNamePublic(Boolean((user as any)?.namePublic));
  }, [user]);

  const handleToggleNamePublic = async (val: boolean) => {
    if (!user) return;
    const stableName = (user.name || profileForm.name || '').trim();
    const stableDaohao = (profileForm.daohao || (user as any)?.daohao || '').trim();
    if (!stableName) {
      setProfileError('请先完善姓名后再设置隐私');
      return;
    }
    if (val && !stableDaohao) {
      setProfileError('请先填写对外道号，再开启公开信息');
      dispatchToast('error', '请先填写对外道号，再开启公开信息');
      return;
    }
    setPrivacySaving(true);
    setProfileError('');
    try {
      const updatedUser = await updateMyProfile({
        name: stableName,
        namePublic: val,
      });
      applyUserSnapshot(updatedUser);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : '隐私设置保存失败');
    } finally {
      setPrivacySaving(false);
    }
  };

  const [notifySettings, setNotifySettings] = useState({ newSolution: true, toolUpdate: true, community: false });

  useEffect(() => {
    if (user) {
      setNotifySettings({
        newSolution: user.notifyNewSolution ?? true,
        toolUpdate: user.notifyToolUpdate ?? true,
        community: user.notifyCommunity ?? false,
      });
    }
  }, [user]);

  const [notifySaving, setNotifySaving] = useState(false);
  const handleToggleNotify = async (key: 'newSolution' | 'toolUpdate' | 'community', val: boolean) => {
    if (!user) return;
    const stableName = (user.name || profileForm.name || '').trim();
    if (!stableName) {
      setProfileError('请先完善姓名后再设置通知');
      return;
    }
    const nextSettings = {
      ...notifySettings,
      [key]: val,
    };
    setNotifySettings(nextSettings);
    setNotifySaving(true);
    setProfileError('');
    try {
      const updatedUser = await updateMyProfile({
        name: stableName,
        notifyNewSolution: nextSettings.newSolution,
        notifyToolUpdate: nextSettings.toolUpdate,
        notifyCommunity: nextSettings.community,
      });
      applyUserSnapshot(updatedUser);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : '通知设置保存失败');
      setNotifySettings(notifySettings);
    } finally {
      setNotifySaving(false);
    }
  };
  const [pwdForm, setPwdForm] = useState({ current: '', next: '', confirm: '' });
  const [pwdMsg, setPwdMsg] = useState('');
  const [showPwd, setShowPwd] = useState({ current: false, next: false, confirm: false });
  const [favTab, setFavTab] = useState<'solution' | 'tool'>('solution');
  const [pointsHistTab, setPointsHistTab] = useState<'all' | 'earn' | 'spend'>('all');
  const [favSearch, setFavSearch] = useState('');
  const [favDiffFilter, setFavDiffFilter] = useState('全部');
  const [favCategoryFilter, setFavCategoryFilter] = useState('全部');
  const [histSearch, setHistSearch] = useState('');
  const [histTypeFilter, setHistTypeFilter] = useState<'all' | 'solution' | 'tool'>('all');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const filteredFavSolutions = favoriteSolutions.filter((s) => {
    const matchSearch = !favSearch || s.title.includes(favSearch) || s.commercial?.includes(favSearch) || s.opensource?.includes(favSearch);
    const matchDiff = favDiffFilter === '全部' || s.difficulty === favDiffFilter;
    return matchSearch && matchDiff;
  });
  const filteredFavTools = favoriteTools.filter((t) => {
    const toolCategoryLabel = getToolCategoryLabel(t.category || '');
    const matchSearch = !favSearch || t.name.includes(favSearch) || t.category?.includes(favSearch) || toolCategoryLabel.includes(favSearch);
    const matchCat = favCategoryFilter === '全部' || t.category === favCategoryFilter;
    return matchSearch && matchCat;
  });

  const inviteFeatureEnabled = switches?.invite === true;
  const pointsReady = !pointsLoading && !pointsError;

  const displayFavoritesCount = stats ? stats.totalFavoritesCount : totalFavs;
  const displayDownloadsCount = stats ? stats.totalDownloadsCount : downloadRecords.length;
  const displayPoints = stats ? stats.currentPoints : (pointsReady ? points : 0);

  const tabs = [
    { key: 'profile', label: '个人信息', icon: 'ri-user-line' },
    { key: 'favorites', label: '我的收藏', icon: 'ri-heart-line', badge: displayFavoritesCount },
    { key: 'downloads', label: '下载记录', icon: 'ri-download-line', badge: displayDownloadsCount },
    { key: 'history', label: '浏览足迹', icon: 'ri-history-line', badge: historyItems.length },
    { key: 'points', label: '灵石中心', icon: 'ri-sparkling-line', badge: displayPoints > 0 ? displayPoints : undefined },
    { key: 'announcements', label: '站内公告', icon: 'ri-megaphone-line' },
    { key: 'settings', label: '账号设置', icon: 'ri-settings-3-line' },
  ] as { key: Tab; label: string; icon: string; badge?: number }[];


  const [pwdSaving, setPwdSaving] = useState(false);

  const handlePwdSave = async () => {
    if (!pwdForm.current || !pwdForm.next || !pwdForm.confirm) {
      setPwdMsg('error:请填写所有密码字段');
      return;
    }
    if (pwdForm.next.length < 8) { setPwdMsg('error:新密码至少 8 位'); return; }
    if (!/[A-Za-z]/.test(pwdForm.next) || !/[0-9]/.test(pwdForm.next)) { setPwdMsg('error:新密码需包含字母和数字'); return; }
    if (pwdForm.next !== pwdForm.confirm) { setPwdMsg('error:两次新密码不一致'); return; }
    if (pwdForm.current === pwdForm.next) {
      setPwdMsg('error:新密码不能与当前密码一致');
      return;
    }
    setPwdSaving(true);
    try {
      await changeMyPassword({
        currentPassword: pwdForm.current,
        newPassword: pwdForm.next,
      });
      setPwdMsg('success:密码已更新，即将重新登录！');
      setPwdForm({ current: '', next: '', confirm: '' });
      setTimeout(() => {
        setPwdMsg('');
        logout();
        navigate('/login');
      }, 1500);
    } catch (err) {
      setPwdMsg(`error:${err instanceof Error ? err.message : '密码更新失败，请稍后重试'}`);
    } finally {
      setPwdSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (user?.role === 'super_admin') {
      window.alert('超级管理员属于系统内置核心角色，只读且不允许注销其账户');
      setShowLogoutConfirm(false);
      return;
    }
    try {
      await deactivateMyAccount();
    } catch (err) {
      console.error('Failed to deactivate account on backend:', err);
      window.alert(err instanceof Error ? err.message : '注销账号失败，请稍后重试');
      setShowLogoutConfirm(false);
      return;
    }

    const userEmail = user?.email || user?.phone || '';
    const keysToRemove = [
      `wanxiang_collect_${userEmail}`,
      `wanxiang_points_${userEmail}`,
      `wanxiang_points_history_${userEmail}`,
      `wanxiang_download_${userEmail}`,
      `wanxiang_history_${userEmail}`,
      `wanxiang_checkin_${userEmail}`,
      `wanxiang_name_public_${userEmail}`,
      `wanxiang_like_${userEmail}`,
      `wanxiang_share_${userEmail}`,
    ];
    keysToRemove.forEach((k) => { try { localStorage.removeItem(k); } catch { /* 忽略错误 */ } });
    logout();
    navigate('/');
  };

  const displayName = user?.name || profileForm.name || '会员';
  const displayDaohao = (user as any)?.daohao || profileForm.daohao || '';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  const [avatarUrl, setAvatarUrl] = useState<string>((user as any)?.avatarUrl || '');
  useEffect(() => {
    setAvatarUrl((user as any)?.avatarUrl || '');
  }, [user]);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement>(null);

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.target;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!user) {
      setProfileError('请先登录');
      inputEl.value = '';
      return;
    }
    if (file.size > MAX_COVER_IMAGE_SIZE_BYTES) {
      setProfileError(`头像大小不能超过 ${MAX_COVER_IMAGE_SIZE_BYTES / 1024 / 1024}MB`);
      inputEl.value = '';
      return;
    }

    const contentType = file.type || 'application/octet-stream';
    setAvatarUploading(true);
    setProfileError('');
    try {
      const presign = await presignUploadFile({
        fileName: file.name,
        contentType,
        fileCategory: 'avatar',
      });
      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (uploadRes.status !== 200) {
        throw new Error(`头像上传失败（${uploadRes.status}）`);
      }
      await completeUploadFile({
        objectKey: presign.objectKey,
        originalName: file.name,
        mimeType: contentType,
        sizeBytes: file.size,
        fileCategory: 'avatar',
      });
      const absoluteUrl = `https://${presign.bucket}.${presign.endpoint}/${presign.objectKey}`;
      const stableName = (user.name || profileForm.name || '').trim();
      if (!stableName) {
        throw new Error('请先完善姓名后再上传头像');
      }
      const updatedUser = await updateMyProfile({
        name: stableName,
        avatarUrl: absoluteUrl,
      });
      applyUserSnapshot(updatedUser);
      setShowAvatarModal(false);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : '头像上传失败，请稍后重试');
    } finally {
      setAvatarUploading(false);
    }
    inputEl.value = '';
  };

  const handleRemoveAvatar = async () => {
    if (!user) return;
    const stableName = (user.name || profileForm.name || '').trim();
    if (!stableName) {
      setProfileError('请先完善姓名后再操作头像');
      return;
    }
    setAvatarUploading(true);
    setProfileError('');
    try {
      const updatedUser = await updateMyProfile({
        name: stableName,
        avatarUrl: null,
      });
      applyUserSnapshot(updatedUser);
      setShowAvatarModal(false);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : '移除头像失败，请稍后重试');
    } finally {
      setAvatarUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      <input ref={avatarFileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />

      {/* 顶部个人背景面板区域 */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] pt-28 pb-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center sm:items-end gap-5">
          <div
            className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-2xl text-white text-3xl font-bold relative group cursor-pointer overflow-hidden"
            onClick={() => setShowAvatarModal(true)}
            title="点击修改头像"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              avatarLetter
            )}
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
              <i className="ri-camera-line text-white text-xl"></i>
              <span className="text-white text-xs mt-1">修改头像</span>
            </div>
          </div>
          <div className="text-center sm:text-left flex-1">
            <div className="flex items-center gap-2 justify-center sm:justify-start flex-wrap">
              <h1 className="text-2xl font-bold text-white">{displayName}</h1>
              {displayDaohao && (
                <span className="px-2 py-0.5 bg-amber-400/20 text-amber-300 text-sm rounded-full font-medium">
                  <i className="ri-sword-line mr-1 text-xs"></i>{displayDaohao}
                </span>
              )}
            </div>
            <p className="text-white/60 text-sm mt-1">
              {profileForm.company && <span>{profileForm.company}</span>}
              {profileForm.company && profileForm.position && <span> · </span>}
              {profileForm.position && <span>{profileForm.position}</span>}
              {!profileForm.company && !profileForm.position && <span>{user?.email || '暂未设置公司信息'}</span>}
            </p>
            <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start flex-wrap">
              <span className="px-2 py-0.5 bg-[#0ea5e9]/20 text-[#38bdf8] text-xs rounded-full">{getTierLabel(user?.membershipTier || '')}</span>
              {pointsReady && (
                <span className="text-xs px-2 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: levelInfo.color }}>
                  <i className={`${levelInfo.icon} mr-1`}></i>{levelInfo.name} · {levelInfo.title}
                </span>
              )}
              <span className="text-white/40 text-xs">欢迎回来</span>
            </div>
          </div>
          <div className="flex gap-6 text-center">
            {[
              { label: '我的收藏', value: displayFavoritesCount, onClick: () => setActiveTab('favorites') },
              { label: '下载工具', value: displayDownloadsCount, onClick: () => setActiveTab('downloads') },
              { label: '我的灵石', value: displayPoints, onClick: () => setActiveTab('points') },
            ].map((s) => (
              <button key={s.label} onClick={s.onClick} className="cursor-pointer hover:opacity-80 transition-opacity">
                <div className="text-2xl font-bold text-white">{s.value}</div>
                <div className="text-white/40 text-xs mt-0.5">{s.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 左侧侧边栏导航 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-gray-100 p-2 sticky top-24">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    activeTab === tab.key ? 'bg-[#0ea5e9]/10 text-[#0ea5e9]' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  <span className="flex-1 text-left">{tab.label}</span>
                  {'badge' in tab && tab.badge !== undefined && tab.badge > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${activeTab === tab.key ? 'bg-[#0ea5e9] text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* 右侧核心页签内容 */}
          <div className="lg:col-span-3">
            {switchesLoading && (
              <div className="mb-5 p-3 bg-gray-50 border border-gray-100 rounded-xl text-sm text-gray-500 flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-gray-200 border-t-[#0ea5e9] rounded-full animate-spin"></span>
                正在加载功能开关...
              </div>
            )}
            {switchesError && (
              <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-500 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {switchesError}
              </div>
            )}

            {/* ── Profile ── */}
            {activeTab === 'profile' && (
              <div className="space-y-5">
                {pointsReady && (
                  <LevelCard points={points} levelInfo={levelInfo} levels={levels} onGoPoints={() => setActiveTab('points')} />
                )}
                <DailyCheckIn onGoPoints={() => setActiveTab('points')} />
                {inviteFeatureEnabled && (
                  <InviteFriend />
                )}

                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-xl text-white text-xl font-bold relative group cursor-pointer overflow-hidden"
                      onClick={() => setShowAvatarModal(true)}
                      title="点击修改头像"
                    >
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                      ) : (
                        avatarLetter
                      )}
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                        <i className="ri-camera-line text-white text-sm"></i>
                      </div>
                    </div>
                    <div>
                      <h2 className="font-bold text-gray-900 text-lg">个人信息</h2>
                      <p className="text-gray-400 text-sm">修改后点击保存立即生效</p>
                    </div>
                  </div>

                  {/* 道号提示 */}
                  <div className="mb-5 p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-start gap-2">
                    <i className="ri-sword-line text-amber-500 mt-0.5 shrink-0"></i>
                    <div>
                      <p className="text-xs font-semibold text-amber-700">关于对外道号</p>
                      <p className="text-xs text-amber-600 mt-0.5">道号是您在社区中对外展示的名字，设置后将优先显示在排行榜等公开场合。道号不可与他人重复，且不能包含「管理员」等敏感词。</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        昵称 / 姓名 <span className="text-red-400 text-xs">*</span>
                      </label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        placeholder="请输入姓名"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        <i className="ri-sword-line text-amber-500 mr-1"></i>
                        对外道号
                        <span className="text-gray-400 text-xs ml-1">（公开展示用）</span>
                      </label>
                      <input
                        type="text"
                        value={profileForm.daohao}
                        onChange={(e) => setProfileForm({ ...profileForm, daohao: e.target.value })}
                        placeholder="设置您的江湖道号"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">邮箱地址</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        readOnly
                        disabled
                        placeholder="邮箱不可修改"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">公司名称</label>
                      <input
                        type="text"
                        value={profileForm.company}
                        onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })}
                        placeholder="请输入公司名称"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">职位</label>
                      <input
                        type="text"
                        value={profileForm.position}
                        onChange={(e) => setProfileForm({ ...profileForm, position: e.target.value })}
                        placeholder="请输入职位"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">手机号码</label>
                      <input
                        type="text"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        placeholder="请输入手机号"
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors"
                      />
                    </div>
                  </div>

                  {profileError && (
                    <div className="mt-4 flex items-center gap-2 text-red-500 text-sm bg-red-50 px-4 py-3 rounded-xl">
                      <i className="ri-error-warning-line shrink-0"></i>
                      {profileError}
                    </div>
                  )}

                  <div className="mt-6 flex items-center gap-3">
                    <button
                      onClick={handleProfileSave}
                      disabled={profileSaving}
                      className="px-6 py-2.5 bg-[#0ea5e9] text-white font-medium rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {profileSaving ? '保存中...' : '保存修改'}
                    </button>
                    {profileSaved && (
                      <span className="text-[#10b981] text-sm flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-line"></i>
                        保存成功，信息已更新！
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Favorites ── */}
            {activeTab === 'favorites' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-bold text-gray-900 text-lg">我的收藏</h2>
                    <span className="text-sm text-gray-400">{totalFavs} 项</span>
                  </div>
                  <div className="flex gap-1 bg-gray-50 rounded-xl p-1 mb-4">
                    {([
                      { key: 'solution', label: '收藏方案', count: favoriteSolutions.length },
                      { key: 'tool', label: '收藏工具', count: favoriteTools.length },
                    ] as const).map((t) => (
                      <button
                        key={t.key}
                        onClick={() => { setFavTab(t.key); setFavSearch(''); setFavDiffFilter('全部'); setFavCategoryFilter('全部'); }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${favTab === t.key ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        {t.label}
                        {t.count > 0 && (
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${favTab === t.key ? 'bg-[#0ea5e9] text-white' : 'bg-gray-200 text-gray-500'}`}>{t.count}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2.5 mb-5">
                    <div className="relative flex-1">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="text"
                        placeholder={favTab === 'solution' ? '搜索方案名称、替代产品...' : '搜索工具名称、分类...'}
                        value={favSearch}
                        onChange={(e) => setFavSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] transition-colors"
                      />
                    </div>
                    {favTab === 'solution' && (
                      <select value={favDiffFilter} onChange={(e) => setFavDiffFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] bg-white cursor-pointer">
                        {difficultyOptions.map((d) => <option key={d} value={d}>{d === '全部' ? '全部难度' : getSolutionDifficultyLabel(d)}</option>)}
                      </select>
                    )}
                    {favTab === 'tool' && (
                      <select value={favCategoryFilter} onChange={(e) => setFavCategoryFilter(e.target.value)} className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] bg-white cursor-pointer">
                        {toolCategoryOptions.map((c) => (
                          <option key={c} value={c}>{c === '全部' ? '全部' : getToolCategoryLabel(c)}</option>
                        ))}
                      </select>
                    )}
                    {(favSearch || favDiffFilter !== '全部' || favCategoryFilter !== '全部') && (
                      <button onClick={() => { setFavSearch(''); setFavDiffFilter('全部'); setFavCategoryFilter('全部'); }} className="px-3 py-2 text-sm text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1">
                        <i className="ri-close-line"></i>清除
                      </button>
                    )}
                  </div>

                  {favTab === 'solution' && (
                    <>
                      {favoriteSolutions.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-4"><i className="ri-heart-line text-3xl text-gray-300"></i></div>
                          <p className="font-medium text-gray-500 mb-1">还没有收藏任何方案</p>
                          <p className="text-sm text-gray-400 mb-4">去方案库找找感兴趣的平替方案吧</p>
                          <Link to="/solutions" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap"><i className="ri-compass-3-line"></i>浏览方案库</Link>
                        </div>
                      ) : filteredFavSolutions.length === 0 ? (
                        <div className="text-center py-10">
                          <i className="ri-search-line text-3xl text-gray-300 block mb-2"></i>
                          <p className="text-sm text-gray-400">没有找到匹配的收藏方案</p>
                          <button onClick={() => { setFavSearch(''); setFavDiffFilter('全部'); setFavCategoryFilter('全部'); }} className="mt-3 text-xs text-[#0ea5e9] cursor-pointer hover:underline">清除筛选</button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredFavSolutions.map((s) => (
                            <div key={s.id} className="group flex gap-4 p-4 border border-gray-100 rounded-2xl hover:border-[#0ea5e9]/30 hover:bg-[#0ea5e9]/5 transition-all">
                              <div className="relative w-24 h-16 shrink-0 rounded-xl overflow-hidden">
                                <img src={s.image} alt={s.title} className="w-full h-full object-cover object-top" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <h3 className="font-semibold text-gray-900 text-sm">{s.title}</h3>
                                    <p className="text-gray-400 text-xs mt-0.5">{s.commercial} → <span className="text-[#0ea5e9]">{s.opensource}</span></p>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    {(() => {
                                      const difficultyItem = getSolutionDifficultyItem(s.difficulty);
                                      const color = difficultyItem?.color || '#64748b';
                                      return (
                                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ color, backgroundColor: `${color}15` }}>
                                          {getSolutionDifficultyLabel(s.difficulty) || '无'}
                                        </span>
                                      );
                                    })()}
                                    <span className="text-xs font-semibold text-[#10b981]">省{s.costSaving}</span>
                                  </div>
                                </div>
                                <div className="flex items-center justify-end mt-2 gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button onClick={() => remove(s.id, 'solution')} className="px-2.5 py-1 text-xs text-red-400 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap"><i className="ri-heart-line mr-1"></i>取消收藏</button>
                                  <Link to={`/solutions/${s.id}`} className="px-2.5 py-1 text-xs text-[#0ea5e9] bg-[#0ea5e9]/10 rounded-lg hover:bg-[#0ea5e9] hover:text-white transition-all cursor-pointer whitespace-nowrap">查看详情</Link>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {favTab === 'tool' && (
                    <>
                      {favoriteTools.length === 0 ? (
                        <div className="text-center py-16">
                          <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-4"><i className="ri-tools-line text-3xl text-gray-300"></i></div>
                          <p className="font-medium text-gray-500 mb-1">还没有收藏任何工具</p>
                          <p className="text-sm text-gray-400 mb-4">去工具库找找好用的部署工具吧</p>
                          <Link to="/tools" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f97316] text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap"><i className="ri-tools-line"></i>浏览工具库</Link>
                        </div>
                      ) : filteredFavTools.length === 0 ? (
                        <div className="text-center py-10">
                          <i className="ri-search-line text-3xl text-gray-300 block mb-2"></i>
                          <p className="text-sm text-gray-400">没有找到匹配的收藏工具</p>
                          <button onClick={() => { setFavSearch(''); setFavCategoryFilter('全部'); }} className="mt-3 text-xs text-[#f97316] cursor-pointer hover:underline">清除筛选</button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {filteredFavTools.map((t) => (
                            <div key={t.id} className="group flex items-center gap-4 p-4 border border-gray-100 rounded-2xl hover:border-[#f97316]/30 hover:bg-[#f97316]/5 transition-all">
                              <div className="w-12 h-12 flex items-center justify-center bg-[#f97316]/10 rounded-xl shrink-0"><i className="ri-tools-fill text-[#f97316] text-xl"></i></div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-gray-900 text-sm">{t.name}</h3>
                                  <span className="text-xs px-1.5 py-0.5 bg-[#f97316]/10 text-[#f97316] rounded-md">
                                    {getToolCategoryLabel(t.category || '')}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">v{t.version} · {t.size}</p>
                              </div>
                              <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => remove(t.id, 'tool')} className="px-2.5 py-1 text-xs text-red-400 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap"><i className="ri-heart-line mr-1"></i>取消收藏</button>
                                <Link to={`/tools/${t.id}`} className="px-2.5 py-1 text-xs text-[#f97316] bg-[#f97316]/10 rounded-lg hover:bg-[#f97316] hover:text-white transition-all cursor-pointer whitespace-nowrap">查看详情</Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {totalFavs > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2"><i className="ri-magic-line text-[#f97316]"></i>你可能还感兴趣</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {solutionPool.filter((s) => !solutionIds.includes(String(s.id))).slice(0, 2).map((s) => (
                        <Link key={s.id} to={`/solutions/${s.id}`} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:border-[#0ea5e9]/30 transition-all cursor-pointer">
                          <img src={s.image} alt={s.title} className="w-10 h-8 object-cover object-top rounded-lg shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{s.title}</p>
                            <p className="text-xs text-[#10b981]">可节省 {s.costSaving}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Downloads ── */}
            {activeTab === 'downloads' && (
              <div className="space-y-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-bold text-gray-900 text-lg">下载记录</h2>
                    <span className="text-sm text-gray-400">{downloadRecords.length} 条记录</span>
                  </div>
                  {downloadRecords.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-4"><i className="ri-download-line text-3xl text-gray-300"></i></div>
                      <p className="font-medium text-gray-500 mb-1">还没有下载记录</p>
                      <p className="text-sm text-gray-400 mb-4">去工具库下载想要的工具吧</p>
                      <Link to="/tools" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f97316] text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap"><i className="ri-tools-line"></i>浏览工具库</Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {downloadedTools.map((record) => {
                        const platformIcons: Record<string, string> = { Windows: 'ri-windows-line', macOS: 'ri-apple-line', Linux: 'ri-terminal-line', Docker: 'ri-box-3-line' };
                        const dateStr = record.downloadedAt
                          ? new Date(record.downloadedAt).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' })
                          : '未知时间';
                        const isAttachment = record.toolCategory === '工具附件' || record.toolCategory === '方案附件';
                        const iconClass = isAttachment ? 'ri-attachment-2 text-indigo-500' : 'ri-tools-fill text-[#f97316]';
                        const iconBg = isAttachment ? 'bg-indigo-50' : 'bg-[#f97316]/10';
                        const detailPath = record.targetType === 'solution'
                          ? `/solutions/${record.targetId}`
                          : `/tools/${record.targetId}`;

                        return (
                          <div key={record.id} className="group flex items-center gap-4 p-4 border border-gray-100 rounded-2xl hover:border-[#f97316]/30 hover:bg-[#f97316]/5 transition-all">
                            <div className={`w-12 h-12 flex items-center justify-center ${iconBg} rounded-xl shrink-0`}><i className={`${iconClass} text-xl`}></i></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-gray-900 text-sm">{record.toolName || '未命名工具'}</h3>
                                {record.toolCategory && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded-md ${isAttachment ? 'bg-indigo-50 text-indigo-500' : 'bg-[#f97316]/10 text-[#f97316]'}`}>{record.toolCategory}</span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                {record.version && <span className="text-xs text-gray-400">v{record.version}</span>}
                                {record.size && <span className="text-xs text-gray-400">{record.size}</span>}
                                <span className="text-xs text-gray-400"><i className="ri-calendar-line mr-1"></i>{dateStr} 下载</span>
                              </div>
                              <div className="flex gap-1 mt-1.5">
                                {record.platform.map((p) => (
                                  <div key={p} className="w-5 h-5 flex items-center justify-center rounded bg-gray-100" title={p}>
                                    <i className={`${platformIcons[p] || 'ri-computer-line'} text-xs text-gray-400`}></i>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                              {record.downloadUrl ? (
                                <a href={record.downloadUrl} target="_blank" rel="nofollow noreferrer" className="px-2.5 py-1 text-xs text-[#10b981] bg-[#10b981]/10 rounded-lg hover:bg-[#10b981] hover:text-white transition-all cursor-pointer whitespace-nowrap"><i className="ri-download-line mr-1"></i>重新下载</a>
                              ) : (
                                <span className="px-2.5 py-1 text-xs text-gray-400 bg-gray-100 rounded-lg whitespace-nowrap">无下载地址</span>
                              )}
                              <Link to={detailPath} className={`px-2.5 py-1 text-xs rounded-lg transition-all cursor-pointer whitespace-nowrap ${isAttachment ? 'text-indigo-500 bg-indigo-50 hover:bg-indigo-500 hover:text-white' : 'text-[#f97316] bg-[#f97316]/10 hover:bg-[#f97316] hover:text-white'}`}>查看详情</Link>
                              <button onClick={() => { void removeDownload(record.targetId, record.targetType); }} className="px-2.5 py-1 text-xs text-red-400 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap"><i className="ri-delete-bin-line"></i></button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── History ── */}
            {activeTab === 'history' && (
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-bold text-gray-900 text-lg">浏览足迹</h2>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400">{historyItems.length} 条记录</span>
                    {historyItems.length > 0 && (
                      <button onClick={() => { void clearHistory(); }} className="text-xs text-red-400 hover:text-red-500 cursor-pointer whitespace-nowrap flex items-center gap-1">
                        <i className="ri-delete-bin-line"></i>清空足迹
                      </button>
                    )}
                  </div>
                </div>
                {historyItems.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2.5 mb-5">
                    <div className="relative flex-1">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input type="text" placeholder="搜索方案/工具名称..." value={histSearch} onChange={(e) => setHistSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] transition-colors" />
                    </div>
                    <div className="flex gap-1 bg-gray-50 rounded-xl p-1">
                      {([['all', '全部'], ['solution', '方案'], ['tool', '工具']] as const).map(([k, label]) => (
                        <button key={k} onClick={() => setHistTypeFilter(k)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${histTypeFilter === k ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}>{label}</button>
                      ))}
                    </div>
                    {(histSearch || histTypeFilter !== 'all') && (
                      <button onClick={() => { setHistSearch(''); setHistTypeFilter('all'); }} className="px-3 py-2 text-sm text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1"><i className="ri-close-line"></i>清除</button>
                    )}
                  </div>
                )}
                {historyItems.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded-2xl mx-auto mb-4"><i className="ri-history-line text-3xl text-gray-300"></i></div>
                    <p className="font-medium text-gray-500 mb-1">还没有浏览记录</p>
                    <p className="text-sm text-gray-400 mb-4">去探索方案库和工具库吧</p>
                    <div className="flex items-center justify-center gap-3">
                      <Link to="/solutions" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0ea5e9] text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap"><i className="ri-compass-3-line"></i>方案库</Link>
                      <Link to="/tools" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f97316] text-white text-sm font-medium rounded-xl cursor-pointer whitespace-nowrap"><i className="ri-tools-line"></i>工具库</Link>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {historyItems.filter((item) => {
                      const mappedCategory = item.type === 'solution'
                        ? getSolutionCategoryLabel(item.category || '')
                        : getToolCategoryLabel(item.category || '');
                      const matchSearch = !histSearch
                        || item.title.toLowerCase().includes(histSearch.toLowerCase())
                        || (item.category && item.category.includes(histSearch))
                        || mappedCategory.includes(histSearch);
                      const matchType = histTypeFilter === 'all' || item.type === histTypeFilter;
                      return matchSearch && matchType;
                    }).map((item) => {
                      const isSolution = item.type === 'solution';
                      const detailPath = isSolution ? `/solutions/${item.id}` : `/tools/${item.id}`;
                      const timeStr = (() => {
                        const d = new Date(item.visitedAt);
                        if (Number.isNaN(d.getTime())) return '未知时间';
                        const now = new Date();
                        const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
                        if (diff < 60) return '刚刚';
                        if (diff < 3600) return `${Math.floor(diff / 60)} 分钟前`;
                        if (diff < 86400) return `${Math.floor(diff / 3600)} 小时前`;
                        return `${Math.floor(diff / 86400)} 天前`;
                      })();
                      const categoryLabel = isSolution
                        ? getSolutionCategoryLabel(item.category || '')
                        : getToolCategoryLabel(item.category || '');
                      return (
                        <div key={`${item.type}-${item.id}`} className="group flex items-center gap-4 p-4 border border-gray-100 rounded-2xl hover:border-gray-200 hover:bg-gray-50/50 transition-all">
                          {isSolution && item.image ? (
                            <div className="w-16 h-12 rounded-xl overflow-hidden shrink-0"><img src={item.image} alt={item.title} className="w-full h-full object-cover object-top" /></div>
                          ) : (
                            <div className={`w-12 h-12 flex items-center justify-center rounded-xl shrink-0 ${isSolution ? 'bg-[#0ea5e9]/10' : 'bg-[#f97316]/10'}`}>
                              <i className={`${isSolution ? 'ri-lightbulb-line text-[#0ea5e9]' : 'ri-tools-line text-[#f97316]'} text-xl`}></i>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900 text-sm">{item.title}</h3>
                              <span className={`text-xs px-1.5 py-0.5 rounded-md ${isSolution ? 'bg-[#0ea5e9]/10 text-[#0ea5e9]' : 'bg-[#f97316]/10 text-[#f97316]'}`}>{isSolution ? '方案' : '工具'}</span>
                              {item.category && <span className="text-xs text-gray-400">{categoryLabel}</span>}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1"><i className="ri-time-line"></i>{timeStr}访问</p>
                          </div>
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                            <Link to={detailPath} className={`px-2.5 py-1 text-xs rounded-lg transition-all cursor-pointer whitespace-nowrap ${isSolution ? 'text-[#0ea5e9] bg-[#0ea5e9]/10 hover:bg-[#0ea5e9] hover:text-white' : 'text-[#f97316] bg-[#f97316]/10 hover:bg-[#f97316] hover:text-white'}`}>再次查看</Link>
                            <button onClick={() => { void removeHistory(item.id, item.type); }} className="w-7 h-7 flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg cursor-pointer transition-all"><i className="ri-close-line text-sm"></i></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Points ── */}
            {activeTab === 'points' && (
              !switches ? (
                switchesError ? (
                  <div className="bg-white rounded-2xl border border-red-100 p-8 text-center text-red-500">
                    功能开关加载失败：{switchesError}
                  </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500 flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-gray-200 border-t-[#0ea5e9] rounded-full animate-spin"></span>
                  正在加载功能开关...
                </div>
              )
            ) : pointsLoading ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-gray-500 flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-gray-200 border-t-[#0ea5e9] rounded-full animate-spin"></span>
                  正在加载灵石数据...
                </div>
              ) : pointsError ? (
                <div className="bg-white rounded-2xl border border-red-100 p-8 text-center text-red-500">
                  灵石数据加载失败：{pointsError}
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 overflow-hidden relative">
                    <div className="absolute inset-0 opacity-5" style={{ backgroundColor: levelInfo.color }}></div>
                    <div className="relative">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 flex items-center justify-center rounded-2xl" style={{ backgroundColor: `${levelInfo.color}20` }}>
                            <i className={`${levelInfo.icon} text-2xl`} style={{ color: levelInfo.color }}></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-900">{levelInfo.name}</span>
                              <span className="text-sm font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: levelInfo.color }}>{levelInfo.title}</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">共修炼 {points.toLocaleString()} 灵石</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-gray-900">{points.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">灵石总量</div>
                        </div>
                      </div>
                      {nextLevel && (
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                            <span>晋升进度</span>
                            <span>距《{nextLevel.name}》还需 {nextLevel.minPoints - points} 灵石</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(progressToNext, 100)}%`, backgroundColor: levelInfo.color }}></div>
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 mt-1">
                            <span>{levelInfo.minPoints}</span>
                            <span>{nextLevel.minPoints}</span>
                          </div>
                        </div>
                      )}
                      {!nextLevel && (
                        <div className="mt-2 flex items-center gap-2 text-[#ef4444] text-sm">
                          <i className="ri-rocket-2-line"></i>
                          <span>封神飞升！山河苍生，天地共色——已达大乘境界！</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2"><i className="ri-sparkling-line text-[#f97316]"></i>修仙境界</h3>
                    {levels.length === 0 ? (
                      <p className="text-sm text-gray-400">暂无等级配置</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {levels.map((l) => {
                          const reached = points >= l.minPoints;
                          const current = levelInfo.level === l.level;
                          return (
                            <div key={l.level} className={`p-3 rounded-xl border-2 transition-all ${current ? '' : reached ? 'border-gray-200 opacity-80' : 'border-gray-100 opacity-50'}`} style={current ? { borderColor: l.color, backgroundColor: `${l.color}08` } : {}}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${l.color}15` }}>
                                  <i className={`${l.icon} text-sm`} style={{ color: reached ? l.color : '#94a3b8' }}></i>
                                </div>
                                {current && <span className="text-xs px-1.5 py-0.5 text-white rounded-full font-medium" style={{ backgroundColor: l.color }}>当前</span>}
                              </div>
                              <div className="text-xs font-bold text-gray-800">{l.name}</div>
                              <div className="text-xs font-medium mt-0.5" style={{ color: reached ? l.color : '#94a3b8' }}>{l.title}</div>
                              <div className="text-xs text-gray-400 mt-0.5">{l.minPoints.toLocaleString()}{l.maxPoints === Infinity ? '+' : `～${l.maxPoints.toLocaleString()}`} 灵石</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2"><i className="ri-add-circle-line text-[#10b981]"></i>如何获得积分</h3>
                    {pointsRules.length === 0 ? (
                      <p className="text-sm text-gray-400">暂无积分规则配置</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {pointsRules.map((rule) => (
                          <div key={rule.key} className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-xl">
                            <div className="w-8 h-8 flex items-center justify-center bg-[#10b981]/10 rounded-lg shrink-0"><i className="ri-add-line text-[#10b981] text-sm"></i></div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-gray-800">{rule.label}</p>
                              <p className="text-xs text-[#10b981] font-semibold">{rule.rewardText}</p>
                              <p className="text-xs text-gray-400">{rule.dailyLimitText}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700 flex items-start gap-2">
                      <i className="ri-shield-check-line shrink-0 mt-0.5"></i>
                      <span>每日每项行为有获分上限，超出后不再加分，有效防止刷分行为。</span>
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2"><i className="ri-time-line text-[#0ea5e9]"></i>积分明细</h3>
                      <span className="text-xs text-gray-400">共 {pointsHistory.length} 条记录</span>
                    </div>
                    {(() => {
                      const filteredHist = pointsHistory.filter((r) => {
                        if (pointsHistTab === 'earn') return r.points > 0;
                        if (pointsHistTab === 'spend') return r.points < 0;
                        return true;
                      });
                      return (
                        <>
                          <div className="flex gap-1 bg-gray-50 rounded-xl p-1 mb-3">
                            {([['all', '全部'], ['earn', '收入'], ['spend', '消费']] as const).map(([k, label]) => (
                              <button key={k} onClick={() => setPointsHistTab(k)} className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${pointsHistTab === k ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}>{label}</button>
                            ))}
                          </div>
                          {filteredHist.length === 0 ? (
                            <div className="text-center py-10">
                              <i className="ri-award-line text-3xl text-gray-300 block mb-2"></i>
                              <p className="text-sm text-gray-400">暂无{pointsHistTab === 'spend' ? '消费' : pointsHistTab === 'earn' ? '收入' : ''}记录</p>
                              {pointsHistTab === 'all' && <p className="text-xs text-gray-300 mt-1">完成已开启的积分行为后，记录将显示在这里</p>}
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                              {filteredHist.slice(0, 100).map((record) => {
                                const safePoints = Number(record.points);
                                const hasValidPoints = Number.isFinite(safePoints);
                                const isSpend = hasValidPoints ? safePoints < 0 : false;
                                const isManual = record.manualBy != null;
                                const iconColor = isSpend ? '#ef4444' : isManual ? '#f59e0b' : '#10b981';
                                const icon = isSpend ? 'ri-copper-coin-line' : isManual ? 'ri-gift-line' : 'ri-add-circle-line';
                                const createdDate = record.createdAt ? new Date(record.createdAt) : null;
                                const createdAtText = createdDate && !Number.isNaN(createdDate.getTime())
                                  ? createdDate.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
                                  : '--';
                                return (
                                  <div key={record.id} className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0 group hover:bg-gray-50/50 px-1 rounded-lg transition-all">
                                    <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: `${iconColor}15` }}>
                                      <i className={`${icon} text-sm`} style={{ color: iconColor }}></i>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-gray-700">{record.action}</p>
                                      {record.targetTitle && <p className="text-xs text-gray-400 truncate">{record.targetTitle}</p>}
                                      {isManual && <p className="text-xs text-amber-500">管理员赠送 · {record.manualBy}</p>}
                                    </div>
                                    <div className="text-right shrink-0">
                                      <div className="text-sm font-bold" style={{ color: iconColor }}>
                                        {hasValidPoints ? (isSpend ? safePoints : `+${safePoints}`) : '--'}
                                      </div>
                                      <div className="text-xs text-gray-400">{createdAtText}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="font-bold text-gray-900 text-sm mb-4 flex items-center gap-2"><i className="ri-medal-line text-amber-500"></i>晋级里程碑</h3>
                    {levels.length === 0 ? (
                      <p className="text-sm text-gray-400">暂无等级配置</p>
                    ) : (
                    <div className="space-y-3">
                      {levels.filter((l) => points >= l.minPoints).map((l) => {
                        const isCurrent = levelInfo.level === l.level;
                        return (
                          <div key={l.level} className="flex items-center gap-3 p-3 rounded-xl" style={isCurrent ? { backgroundColor: `${l.color}08`, border: `1px solid ${l.color}30` } : { backgroundColor: '#f9fafb' }}>
                            <div className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: `${l.color}20` }}><i className={`${l.icon} text-lg`} style={{ color: l.color }}></i></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold" style={{ color: l.color }}>{l.name}</span>
                                <span className="text-xs text-gray-400">{l.title}</span>
                                {isCurrent && <span className="text-xs px-1.5 py-0.5 text-white rounded-full font-medium" style={{ backgroundColor: l.color }}>当前</span>}
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">达到 {l.minPoints.toLocaleString()} 灵石时解锁</p>
                            </div>
                            <div className="text-right shrink-0"><i className="ri-checkbox-circle-fill text-lg" style={{ color: l.color }}></i></div>
                          </div>
                        );
                      })}
                      {levels.filter((l) => points < l.minPoints).map((l) => (
                        <div key={l.level} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl opacity-40">
                          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-200 shrink-0"><i className={`${l.icon} text-lg text-gray-400`}></i></div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-400">{l.name}</span>
                              <span className="text-xs text-gray-300">{l.title}</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5">还需 {(l.minPoints - points).toLocaleString()} 灵石解锁</p>
                          </div>
                          <div className="text-right shrink-0"><i className="ri-lock-line text-gray-300 text-lg"></i></div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                </div>
              )
            )}

            {/* ── Announcements ── */}
            {activeTab === 'announcements' && (
              <AnnouncementCenter />
            )}

            {/* ── Settings ── */}
            {activeTab === 'settings' && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="font-bold text-gray-900 text-lg mb-5">修改密码</h2>
                  <div className="space-y-4 max-w-sm">
                    {[
                      { label: '当前密码', key: 'current' as const },
                      { label: '新密码', key: 'next' as const },
                      { label: '确认新密码', key: 'confirm' as const },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">{f.label}</label>
                        <div className="relative">
                          <input
                            type={showPwd[f.key] ? 'text' : 'password'}
                            value={pwdForm[f.key]}
                            onChange={(e) => setPwdForm({ ...pwdForm, [f.key]: e.target.value })}
                            placeholder={`请输入${f.label}`}
                            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors pr-10"
                          />
                          <button type="button" onClick={() => setShowPwd((p) => ({ ...p, [f.key]: !p[f.key] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                            <i className={`${showPwd[f.key] ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                          </button>
                        </div>
                      </div>
                    ))}
                    {pwdMsg && (
                      <div className={`text-sm flex items-center gap-1.5 ${pwdMsg.startsWith('error') ? 'text-red-500' : 'text-[#10b981]'}`}>
                        <i className={pwdMsg.startsWith('error') ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'}></i>
                        {pwdMsg.replace(/^(error|success):/, '')}
                      </div>
                    )}
                    <button onClick={() => { void handlePwdSave(); }} disabled={pwdSaving} className="px-6 py-2.5 bg-[#0ea5e9] text-white font-medium rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">{pwdSaving ? '更新中...' : '更新密码'}</button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="font-bold text-gray-900 text-lg mb-2">隐私设置</h2>
                  <p className="text-gray-500 text-sm mb-4">控制您在对外展示页面的个人信息</p>
                  <div className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors border border-gray-100">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#8b5cf6]/10 rounded-lg shrink-0 mt-0.5"><i className="ri-eye-line text-[#8b5cf6] text-sm"></i></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">公开信息</p>
                        <p className="text-xs text-gray-500 mt-0.5">开启后，公开页面将按您的对外道号进行展示；关闭后显示为「匿名道友」</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { void handleToggleNamePublic(!namePublic); }}
                      disabled={privacySaving}
                      className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors shrink-0 ml-3 disabled:opacity-60 disabled:cursor-not-allowed ${namePublic ? 'bg-[#8b5cf6]' : 'bg-gray-200'}`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${namePublic ? 'left-6' : 'left-1'}`}></span>
                    </button>
                  </div>
                  {privacySaving && (
                    <p className="mt-2 text-xs text-gray-400">正在保存隐私设置...</p>
                  )}
                  {!profileForm.daohao.trim() && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-xl flex items-center gap-2">
                      <i className="ri-information-line text-amber-500 shrink-0"></i>
                      <p className="text-xs text-amber-700">如需开启公开信息，请先在上方填写对外道号。</p>
                    </div>
                  )}
                  {namePublic && (
                    <div className="mt-3 p-3 bg-[#8b5cf6]/5 border border-[#8b5cf6]/20 rounded-xl flex items-center gap-2">
                      <i className="ri-information-line text-[#8b5cf6] shrink-0"></i>
                      <p className="text-xs text-[#8b5cf6]">您的对外道号「{profileForm.daohao.trim() || displayName}」将在公开页面中展示。</p>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 p-6">
                  <h2 className="font-bold text-gray-900 text-lg mb-2">通知设置</h2>
                  <p className="text-gray-500 text-sm mb-4">管理您的邮件通知偏好</p>
                  <div className="space-y-4">
                    {[
                      { key: 'newSolution', label: '新方案发布通知', desc: '有新的平替方案时通知我' },
                      { key: 'toolUpdate', label: '工具更新通知', desc: '工具有新版本时通知我' },
                      { key: 'community', label: '社区动态', desc: '社区有新讨论时通知我' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                        </div>
                        <button
                          onClick={() => {
                            const k = item.key as 'newSolution' | 'toolUpdate' | 'community';
                            void handleToggleNotify(k, !(notifySettings as any)[k]);
                          }}
                          disabled={notifySaving}
                          className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors shrink-0 ml-3 disabled:opacity-60 disabled:cursor-not-allowed ${(notifySettings as Record<string, boolean>)[item.key] ? 'bg-[#0ea5e9]' : 'bg-gray-200'}`}
                        >
                          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${(notifySettings as Record<string, boolean>)[item.key] ? 'left-6' : 'left-1'}`}></span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-red-100 p-6">
                  <h2 className="font-bold text-red-500 text-base mb-2">危险操作</h2>
                  <p className="text-gray-500 text-sm mb-4">注销账号后，您的收藏、足迹、灵石等所有本地数据将被清除，无法恢复，请谨慎操作。</p>
                  <button onClick={() => setShowLogoutConfirm(true)} className="px-5 py-2 border border-red-200 text-red-500 text-sm font-medium rounded-xl hover:bg-red-50 cursor-pointer whitespace-nowrap">注销账号</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />

      {/* 注销账号确认 Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowLogoutConfirm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 flex items-center justify-center bg-red-50 rounded-2xl mx-auto mb-4"><i className="ri-user-unfollow-line text-red-500 text-2xl"></i></div>
            <h3 className="font-bold text-gray-900 text-center text-lg mb-2">确认注销账号</h3>
            <div className="text-sm text-gray-500 text-center mb-4 space-y-1">
              <p>注销后以下本地数据将被清除：</p>
              <div className="mt-2 p-3 bg-red-50 rounded-xl text-left space-y-1">
                {['收藏方案与工具', '下载记录', '浏览足迹', '灵石和积分记录', '签到记录'].map((item) => (
                  <p key={item} className="text-xs text-red-500 flex items-center gap-1.5"><i className="ri-close-circle-line"></i>{item}</p>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">此操作不可撤销，请确认。</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">再想想</button>
              <button onClick={handleDeactivate} className="flex-1 py-2.5 bg-red-500 text-white text-sm font-medium rounded-xl hover:bg-red-600 cursor-pointer whitespace-nowrap">确认注销</button>
            </div>
          </div>
        </div>
      )}

      {/* 头像管理弹窗 */}
      {showAvatarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAvatarModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-gray-900 text-base">修改头像</h3>
              <button onClick={() => setShowAvatarModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500"></i></button>
            </div>
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] rounded-2xl text-white text-3xl font-bold overflow-hidden">
                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" /> : avatarLetter}
              </div>
            </div>
            <div className="space-y-3">
              <button disabled={avatarUploading} onClick={() => avatarFileRef.current?.click()} className="w-full flex items-center gap-3 px-4 py-3 border-2 border-dashed border-[#0ea5e9]/40 rounded-xl hover:border-[#0ea5e9] hover:bg-[#0ea5e9]/5 cursor-pointer transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0ea5e9]/10 rounded-xl shrink-0"><i className="ri-upload-cloud-line text-[#0ea5e9] text-lg"></i></div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-800">{avatarUploading ? '上传中...' : '上传本地图片'}</p>
                  <p className="text-xs text-gray-400">支持 JPG、PNG、GIF，建议正方形图片</p>
                </div>
              </button>
              {avatarUrl && (
                <button disabled={avatarUploading} onClick={() => { void handleRemoveAvatar(); }} className="w-full flex items-center gap-3 px-4 py-3 border border-red-100 rounded-xl hover:bg-red-50 cursor-pointer transition-all disabled:opacity-60 disabled:cursor-not-allowed">
                  <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-xl shrink-0"><i className="ri-delete-bin-line text-red-400 text-lg"></i></div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-red-500">{avatarUploading ? '处理中...' : '移除当前头像'}</p>
                    <p className="text-xs text-gray-400">恢复为默认字母头像</p>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
